import TopSection from "../../LT/TopSection";
import { Card, Box, Typography } from "@mui/material";
import { SapIbpTableHeaders } from "./SapIbpTableHeaders";
import DataTable from "../../../components/common/datatable/DataTable";
import ButtonComponent from "../../../components/common/button/Button";
import ReplayIcon from "@mui/icons-material/Replay";
import RefreshIcon from "@mui/icons-material/Refresh";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState, useCallback, useRef } from "react";
import { fetchSapIbpSyncStatus } from "../../../redux/actions/sapIbpSyncStatusSlice";
import { postSupplierConfirmation } from "../../../redux/actions/supplierConfirmationSlice";
import { toast } from "react-toastify";

const SAP_IBP_TABLE_FILTERS_STORAGE_KEY = "SAPIBPTableFilters";

const readSapIbpTableFiltersFromStorage = () => {
  try {
    const raw = localStorage.getItem(SAP_IBP_TABLE_FILTERS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const writeSapIbpTableFiltersToStorage = (payload) => {
  try {
    localStorage.setItem(SAP_IBP_TABLE_FILTERS_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Ignore storage failures and keep the page usable.
  }
};

const normalizeTransactionId = (value) => {
  if (value == null) return null;
  const asString = String(value).trim();
  return asString.length ? asString : null;
};

const buildSyncStatusRows = (rawRows) =>
  rawRows.map((row, index) => ({
    id: row?.id ?? `${row?.plant || ""}-${row?.material || ""}-${index}`,
    retries: row?.retries ?? row?.retryCount ?? null,
    ...row,
  }));

const buildSupplierConfirmedPayload = (selectedRows) => {
  if (!Array.isArray(selectedRows)) return [];

  const ids = selectedRows
    .map((row) => normalizeTransactionId(row?.transactionId ?? row?.TRANSACTIONID))
    .filter(Boolean);

  return Array.from(new Set(ids));
};

const SapIbpSyncStatus = () => {
  const columns = SapIbpTableHeaders();
  const dispatch = useDispatch();
  const syncStatusData = useSelector((state) => state.sapIbpSyncStatus?.data);
  const syncStatusLoading = useSelector((state) => state.sapIbpSyncStatus?.loading ?? false);
  const syncStatusError = useSelector((state) => state.sapIbpSyncStatus?.error ?? null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [rows, setRows] = useState([]);
  const [gridApi, setGridApi] = useState(null);
  const [gridFilterModel, setGridFilterModel] = useState(() => readSapIbpTableFiltersFromStorage());
  const [isGridFilterHydrated, setIsGridFilterHydrated] = useState(() => {
    const saved = readSapIbpTableFiltersFromStorage();
    return !(saved && Object.keys(saved).length);
  });
  const isRestoringGridFiltersRef = useRef(false);
  const hasRestoredGridFiltersRef = useRef(false);
  const hasActiveGridFilters = Boolean(
    gridFilterModel && Object.keys(gridFilterModel).length
  );

  const reloadSyncStatusData = (showSuccessToast = false) => {
    dispatch(fetchSapIbpSyncStatus({}))
      .unwrap()
      .then(() => {
        if (showSuccessToast) {
          toast.success("Data has been refreshed", { autoClose: 4000 });
        }
      })
      .catch(() => {
        if (showSuccessToast) {
          toast.error("Refresh failed", { autoClose: 4000 });
        }
      });
  };

  useEffect(() => {
    reloadSyncStatusData();
  }, [dispatch]);

  useEffect(() => {
    const rawRows = Array.isArray(syncStatusData?.data)
      ? syncStatusData.data
      : Array.isArray(syncStatusData)
        ? syncStatusData
        : [];
    setRows(buildSyncStatusRows(rawRows));
  }, [syncStatusData]);

  useEffect(() => {
    writeSapIbpTableFiltersToStorage(gridFilterModel);
  }, [gridFilterModel]);

  const restoreGridFiltersToUi = useCallback(
    (api) => {
      if (!api) return;
      const hasSavedGridFilters = Boolean(
        gridFilterModel && Object.keys(gridFilterModel).length
      );

      if (!hasSavedGridFilters) {
        hasRestoredGridFiltersRef.current = true;
        setIsGridFilterHydrated(true);
        return;
      }

      isRestoringGridFiltersRef.current = true;
      try {
        api.setFilterModel(gridFilterModel);
      } catch (e) {}

      window.setTimeout(() => {
        try {
          api.onFilterChanged();
        } catch (e) {}
        const appliedModel = api.getFilterModel?.() || {};
        const hasApplied = Object.keys(appliedModel).length > 0;
        setGridFilterModel(hasApplied ? appliedModel : null);
        isRestoringGridFiltersRef.current = false;
        hasRestoredGridFiltersRef.current = true;
        setIsGridFilterHydrated(true);
      }, 0);
    },
    [gridFilterModel]
  );

  const handleGridReady = (params) => {
    const api = params?.api ?? null;
    setGridApi(api);
    hasRestoredGridFiltersRef.current = false;
    const hasSavedGridFilters = Boolean(
      gridFilterModel && Object.keys(gridFilterModel).length
    );
    setIsGridFilterHydrated(!hasSavedGridFilters);
  };

  const handleFirstDataRendered = useCallback(
    (params) => {
      if (hasRestoredGridFiltersRef.current) return;
      restoreGridFiltersToUi(params?.api || gridApi);
    },
    [gridApi, restoreGridFiltersToUi]
  );

  useEffect(() => {
    if (!gridApi) return;
    if (isGridFilterHydrated) return;
    if (hasRestoredGridFiltersRef.current) return;
    restoreGridFiltersToUi(gridApi);
  }, [gridApi, isGridFilterHydrated, restoreGridFiltersToUi]);

  const handleGridFilterChanged = useCallback((params) => {
    if (isRestoringGridFiltersRef.current) return;
    const model = params?.api?.getFilterModel?.() || {};
    const hasFilters = Object.keys(model).length > 0;
    setGridFilterModel(hasFilters ? model : null);
  }, []);

  const handleRetry = () => {
    const payload = buildSupplierConfirmedPayload(selectedRows);
    if (!payload.length) return;

    dispatch(postSupplierConfirmation({ data: payload }))
      .unwrap()
      .then(() => {
        dispatch(fetchSapIbpSyncStatus({}));
        setSelectedRows([]);
        gridApi?.deselectAll?.();
        toast.success("Data has been retried successfully", { autoClose: 4000 });
      })
      .catch((error) => {
        console.error("Supplier confirmation retry failed", error);
        toast.error("Retry failed", { autoClose: 4000 });
      });
  };

  const handleSelectionChanged = (params) => {
    const selected = params?.api?.getSelectedRows?.() || [];
    setSelectedRows(selected);
  };

  const handleClearAllFilters = () => {
    if (gridApi) {
      try {
        gridApi.setFilterModel(null);
        gridApi.onFilterChanged?.();
      } catch (e) {}
    }
    setGridFilterModel(null);
  };

  return (
    <>
      <TopSection
        labelList={[
          { title: "Dashboard", path: "/dashboard" },
          { title: "SAP IBP Sync Status" },
        ]}
        hideHeader={true} 
              
      />

      <Box sx={{ width: "100%" }}>
        <Card
          sx={{
           // width: "80%",
            borderRadius: 2,
            margin: "2px auto",
            marginBottom: 2,
            padding: 2,
            minHeight: 56,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
           <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, ml: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 400, color: "text.secondary" }}>
                Integration
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                IBP Sync Status
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 400, color: "text.secondary" }}>
                All user-triggered syncs from the Supplier Detail view
              </Typography>
            </Box>
            <Box sx={{ marginLeft: "auto", display: "flex", gap: 1 }}>
              <ButtonComponent
                label="Refresh"
                type="secondary"
                bgColor="#f9081c"
                color="white"
                height="32px"
                maxWidth="120px"
                styles={{ width: "auto", minWidth: "90px" }}
                endIcon={<RefreshIcon fontSize="small" />}
                onClick={() => reloadSyncStatusData(true)}
              />
              <ButtonComponent
                label="Retry"
                type="primary"
                bgColor="#2563eb"
                color="white"
                height="32px"
                maxWidth="120px"
                styles={{ width: "auto", minWidth: "80px" }}
                endIcon={<ReplayIcon fontSize="small" />}
                onClick={handleRetry}
                disabled={!selectedRows.length}
              />
              <ButtonComponent
                label="Clear All Filters"
                type="secondary"
                bgColor="#4b5563"
                color="white"
                height="32px"
                maxWidth="170px"
                styles={{ width: "auto", minWidth: "150px" }}
                
                onClick={handleClearAllFilters}
                disabled={!hasActiveGridFilters}
              />
            </Box>
        </Box>
      </Card>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          //  width: "83%",
            margin: "0 auto",
            pb: 3,
           //  border:"1px solid #E0E0E0",
          }}
        >
         <Box sx={{ height: 420, width: "100%" }}>
          <DataTable
            height={420}
            rows={rows}
            columns={columns}
            paginationProp={true}
            paginationPageSize={50}
            onSelectionChanged={handleSelectionChanged}
            onGridReady={handleGridReady}
            onFirstDataRendered={handleFirstDataRendered}
            onFilterChanged={handleGridFilterChanged}
            immutableData={true}
            getRowId={(params) => params?.data?.id}
            defaultColDef={{ flex: 1, minWidth: 120, resizable: true,
              filter: 'agSetColumnFilter',
                  filterParams: {
                    excelMode: 'windows',
                    suppressMiniFilter: false,
                    buttons: ['apply', 'reset'],
                  },
             }}
          />
        </Box>
        {syncStatusLoading && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            Loading sync status...
          </Typography>
        )}
        {syncStatusError && !syncStatusLoading && (
          <Typography variant="caption" color="error" sx={{ mt: 1 }}>
            Failed to load sync status.
          </Typography>
        )}
      </Box>
        </Box>
        </>
  );
};


export default SapIbpSyncStatus;